import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { InstitutionResponse, ReportResponse } from '../../../../server/response/responseSchema';
import currencyStore from '../../../stores/currencyStore';
import Box from '../../../uikit/Box';
import Frame from '../../../uikit/Frame';
import { apiClient } from '../../../clientUtils/apiClient';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import stringUtil from '../../../clientUtils/stringUtil';
import HoldingListTable from './HoldingListTable';

class ViewStore {
    readonly institutionSlug: string;
    readonly reportYear: number;
    readonly reportQuarter: number;

    institution: InstitutionResponse | undefined;
    report: ReportResponse | undefined;

    constructor(institutionSlug: string, reportYear: number, reportQuarter: number) {
        this.institutionSlug = institutionSlug;
        this.reportYear = reportYear;
        this.reportQuarter = reportQuarter;

        makeAutoObservable(this);
    }

    async fetch() {
        if (!this.institutionSlug) {
            return;
        }

        const [institution, report] = await Promise.all([
            apiClient.institution.public.getInstitution.$query({
                institutionSlug: this.institutionSlug,
            }),
            apiClient.report.public.getReportAt.$query({
                institutionSlug: this.institutionSlug,
                reportYear: this.reportYear,
                reportQuarter: this.reportQuarter,
            }),
        ]);

        runInAction(() => {
            this.institution = institution;
            this.report = report;
        });
    }
}

const ReportDetailPage = observer(() => {
    const { institutionSlug, reportYear, reportQuarter } = useParams();
    const viewStore = useMobxInstance(
        ViewStore,
        institutionSlug as string,
        Number(reportYear),
        Number(reportQuarter),
    );

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const institutionName = viewStore.institution?.nameKo || viewStore.institution?.name;

    return (
        <Frame>
            <Frame.Title title={institutionName} />
            <Box>
                <div className="flex flex-col gap-2">
                    <div className="text-lg font-semibold">
                        {viewStore.report?.reportYear}년 {viewStore.report?.reportQuarter}
                        분기
                    </div>
                    <div>
                        보유 종목: {stringUtil.toCommaNumber(viewStore.report?.totalHolding)}개
                    </div>
                    <div>
                        보유 가치:{' '}
                        {currencyStore.toCompactUsdOrKrw(
                            Number(viewStore.report?.totalValue) * 1000,
                        )}
                    </div>
                </div>
            </Box>
            <Box>
                <HoldingListTable
                    institutionSlug={viewStore.institutionSlug}
                    reportYear={viewStore.reportYear}
                    reportQuarter={viewStore.reportQuarter}
                />
            </Box>
        </Frame>
    );
});

export default ReportDetailPage;
